// The injectable clock (D-020, ARCH §8.3, TEST_PLAN §8.4). Nothing in @sl/core or the apps reads the system
// time directly — ESLint forbids `Date.now()`, `new Date()` without arguments and `Math.random()`. Code that
// needs "now" takes a `Clock`; tests, the parity crawl and `?demoNow=` pass a fixed one (DEMO_NOW lives in
// tests/fixtures/demo-clock.json, QA-owned). Engine functions go one step further and take the instant itself
// as a parameter (ARCH §2.4), so they never see a Clock at all.

export interface Clock {
  /** The current instant as milliseconds since the Unix epoch (UTC) — the same unit as `Date.now()`. */
  now(): number;
}

/** A clock that stands still until it is told to move. For tests, the parity crawl and `?demoNow=`. */
export interface FixedClock extends Clock {
  set(instant: string | number): void;
  advance(ms: number): void;
}

function toEpochMs(instant: string | number): number {
  const ms = typeof instant === 'number' ? instant : new Date(instant).getTime();
  if (!Number.isFinite(ms))
    throw new Error(`fixedClock: "${String(instant)}" is not a valid instant`);
  return ms;
}

/** `fixedClock('2026-09-22T00:30:00-05:00')` — an ISO instant (with an offset or `Z`) or epoch milliseconds. */
export function fixedClock(instant: string | number): FixedClock {
  let current = toEpochMs(instant);
  return {
    now: () => current,
    set: (next) => {
      current = toEpochMs(next);
    },
    advance: (ms) => {
      current += ms;
    },
  };
}
